import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Phone, ShieldCheck, FilePenLine, Scale, CalendarClock, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { GoldButton, GhostButton } from "../components/Buttons";
import { WhatsAppChatLink } from "../components/WhatsAppButton";
import Seo from "../components/Seo";
import { MaskedLines, Reveal, EASE } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { AMENDMENT, CONTACT, IMAGES, AMENDMENT_FAQ } from "../data/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, required, error, children }) => (
    <div className="space-y-2">
        <Label htmlFor={id} className="text-[12px] uppercase tracking-[0.16em] text-[#737985]">
            {label} {required ? <span className="text-[#8A6D2F]">*</span> : <span className="text-[#9AA0A6]">(optional)</span>}
        </Label>
        {children}
        {error ? (
            <p className="text-[#B4433A] text-xs leading-relaxed" data-testid={`error-${id}`}>{error}</p>
        ) : null}
    </div>
);

const KNOW_CARDS = [
    {
        icon: CalendarClock,
        title: "Eligibility & deadlines",
        text: "Amended returns are subject to filing deadlines that depend on your circumstances and applicable tax law. We assess your specific deadlines with you — don't assume you're out of options without asking.",
    },
    {
        icon: FilePenLine,
        title: "How the review works",
        text: "We examine your original filing carefully, identify potential corrections or missed benefits, and explain your available options in plain language before anything is prepared.",
    },
    {
        icon: Scale,
        title: "No outcome promises",
        text: "Every situation is different. We focus on reviewing your individual case — we do not guarantee a refund, tax savings, or IRS approval of any amendment.",
    },
];

export default function TaxAmendment() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", tax_years: "", message: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((p) => (p[key] ? { ...p, [key]: null } : p));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your full name.";
        if (!form.email.trim()) e.email = "Please enter your email address.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = "Please enter a valid email address, e.g. you@example.com.";
        if (!form.tax_years.trim()) e.tax_years = "Please tell us which tax year or years the return was for.";
        if (!form.message.trim()) e.message = "Please briefly describe the issue with your original return.";
        return e;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) {
            toast.error("Please fix the highlighted fields and try again.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(
                `${API}/inquiries`,
                {
                    inquiry_type: "amendment",
                    name: form.name.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim() || null,
                    tax_years: form.tax_years.trim(),
                    service: "Amended Tax Return",
                    message: form.message.trim(),
                },
                { timeout: 45000 },
            );
            setSubmitted(true);
            toast.success("Amendment inquiry received — we'll be in touch shortly.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (Array.isArray(detail)) {
                const map = {};
                detail.forEach((d) => {
                    const f = d.loc?.[d.loc.length - 1];
                    if (typeof f === "string") map[f] = "Please check this field and try again.";
                });
                setErrors(map);
                toast.error("Please fix the highlighted fields and try again.");
            } else if (err?.response) {
                toast.error(err.response.status >= 500 ? "We hit a snag on our side — please try again in a moment, or call us." : "Could not send your inquiry. Please check your details and try again, or call us.");
            } else {
                toast.error("Network issue — please check your connection and try again, or call us.");
            }
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setForm({ name: "", email: "", phone: "", tax_years: "", message: "" });
        setErrors({});
        setSubmitted(false);
    };

    return (
        <div data-testid="page-tax-amendment">
            <Seo
                title="Free Tax Amendment Review | Freedom Tax Filer"
                description="Made a mistake on a filed return or missed a deduction? Start with a free initial review — no obligation to proceed — and find out if amending makes sense."
                path="/tax-amendment"
            />
            {/* Hero */}
            <section className="relative pt-44 pb-20 lg:pb-28 bg-[#111318] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,164,93,0.10),transparent_60%)]" />
                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">{AMENDMENT.eyebrow}</p>
                        <div className="gold-rule mt-5" />
                    </Reveal>
                    <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.06] tracking-tight mt-8 max-w-4xl">
                        <MaskedLines
                            delay={0.2}
                            lines={[
                                "Already Filed Your Taxes?",
                                <>
                                    You May Still Have <em className="italic text-gold-gradient">Options.</em>
                                </>,
                            ]}
                        />
                    </h1>
                    <Reveal delay={0.45}>
                        <p className="mt-8 sm:mt-10 max-w-3xl text-white/65 text-base sm:text-lg leading-relaxed" data-testid="amendment-hero-intro">
                            Made a mistake on a previous tax return or missed eligible deductions and credits? Freedom Tax
                            Filer can <span className="text-[#C8A45D]">review your original return</span>, identify potential
                            corrections, and help determine whether filing an amended return is appropriate for your situation.
                        </p>
                        <div className="mt-9 sm:mt-10">
                            <GoldButton asChild data-testid="amendment-hero-cta">
                                <a href="#amendment-form">
                                    {AMENDMENT.cta} <ArrowRight className="h-4 w-4" />
                                </a>
                            </GoldButton>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Form + what to know */}
            <section id="amendment-form" className="section-pad scroll-mt-28" data-testid="amendment-form-section">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                    <div className="lg:col-span-5 space-y-5">
                        <SectionHeading eyebrow="Start Your Review" title="Tell us about the original return." />
                        <Reveal delay={0.08}>
                            <p className="text-[#4A4F58] leading-relaxed">{AMENDMENT.support}</p>
                        </Reveal>
                        {KNOW_CARDS.map((c, i) => {
                            const Icon = c.icon;
                            return (
                                <Reveal key={c.title} delay={0.12 + i * 0.06}>
                                    <div className="bg-white border border-[#E7E4DC] shadow-sm rounded-xl p-6 flex items-start gap-4" data-testid={`amendment-know-card-${i}`}>
                                        <span className="h-10 w-10 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center shrink-0">
                                            <Icon className="h-4 w-4 text-[#8A6D2F]" />
                                        </span>
                                        <span>
                                            <span className="block font-display text-lg text-[#20242B]">{c.title}</span>
                                            <span className="block text-[#737985] text-sm leading-relaxed mt-1.5">{c.text}</span>
                                        </span>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            <div className="bg-white border border-[#E7E4DC] shadow-xl p-7 sm:p-10" data-testid="amendment-form-card">
                                {submitted ? (
                                    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }} className="text-center py-14" data-testid="amendment-form-success">
                                        <CheckCircle2 className="h-14 w-14 text-[#C8A45D] mx-auto" />
                                        <h2 className="font-display text-3xl text-[#20242B] mt-6">Amendment inquiry received.</h2>
                                        <p className="text-[#737985] leading-relaxed mt-4 max-w-md mx-auto">
                                            Thank you — we'll review the details you provided and reach out about the next
                                            steps for your original return.
                                        </p>
                                        <GoldButton className="mt-9" onClick={reset} data-testid="amendment-form-again">
                                            Send Another Inquiry <ArrowRight className="h-4 w-4" />
                                        </GoldButton>
                                    </motion.div>
                                ) : (
                                    <>
                                        <div className="border-b border-[#E7E4DC] pb-7">
                                            <p className="eyebrow">Amendment Inquiry</p>
                                            <h2 className="font-display text-2xl sm:text-3xl text-[#20242B] mt-3">Request a review of your return.</h2>
                                            <p className="text-[#737985] text-sm mt-2">A person reads every message — no bots, no queues.</p>
                                        </div>

                                        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="amendment-form" noValidate>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                                <Field id="name" label="Full Name" required error={errors.name}>
                                                    <Input id="name" required maxLength={120} autoComplete="name" value={form.name} onChange={set("name")} placeholder="Jane Doe" aria-invalid={!!errors.name} className="input-dark" data-testid="amendment-input-name" />
                                                </Field>
                                                <Field id="email" label="Email Address" required error={errors.email}>
                                                    <Input id="email" type="email" required autoComplete="email" autoCapitalize="none" value={form.email} onChange={set("email")} placeholder="you@example.com" aria-invalid={!!errors.email} className="input-dark" data-testid="amendment-input-email" />
                                                </Field>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                                <Field id="phone" label="Phone Number" error={errors.phone}>
                                                    <Input id="phone" type="tel" inputMode="tel" autoComplete="tel" maxLength={40} value={form.phone} onChange={set("phone")} placeholder="Your phone number (optional)" className="input-dark" data-testid="amendment-input-phone" />
                                                </Field>
                                                <Field id="tax_years" label="Tax Year(s)" required error={errors.tax_years}>
                                                    <Input id="tax_years" required maxLength={60} value={form.tax_years} onChange={set("tax_years")} placeholder="e.g., 2023, 2024, or 2025" aria-invalid={!!errors.tax_years} className="input-dark" data-testid="amendment-input-tax-years" />
                                                </Field>
                                            </div>

                                            <Field id="message" label="Tell Us About the Issue" required error={errors.message}>
                                                <Textarea id="message" required maxLength={5000} rows={6} value={form.message} onChange={set("message")} placeholder="Briefly describe what may have been missed or reported incorrectly on your original return." aria-invalid={!!errors.message} className="input-dark h-auto py-3 resize-y" data-testid="amendment-textarea-message" />
                                            </Field>

                                            <div className="pt-2">
                                                <GoldButton type="submit" disabled={loading} className="w-full sm:w-auto disabled:opacity-50" data-testid="amendment-submit">
                                                    {loading ? "Submitting…" : AMENDMENT.cta}
                                                    <ArrowRight className="h-4 w-4" />
                                                </GoldButton>
                                                <p className="text-[#9AA0A6] text-xs leading-relaxed mt-5" data-testid="amendment-privacy-note">
                                                    By submitting this form, you agree that Freedom Tax Filer may contact you
                                                    regarding your inquiry. Please do not include Social Security numbers, tax
                                                    identification numbers, passwords, or sensitive tax documents in this form.
                                                    Eligibility, filing deadlines, and possible outcomes depend on your individual
                                                    circumstances and applicable law — we don't guarantee refunds, tax savings,
                                                    or IRS approval.
                                                </p>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Amendment FAQ */}
            <section className="pb-24 lg:pb-32 bg-[#172337] border-y border-white/10" data-testid="amendment-faq">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading
                        dark
                        eyebrow="Common Questions"
                        title={<>Before you inquire, <em className="italic text-gold-gradient">quick answers.</em></>}
                    />
                    <div className="max-w-3xl mx-auto mt-12">
                        <Accordion type="single" collapsible className="border-t border-white/10">
                            {AMENDMENT_FAQ.map((f, i) => (
                                <AccordionItem key={i} value={`amendment-faq-${i}`} className="border-b border-white/10" data-testid={`amendment-faq-item-${i}`}>
                                    <AccordionTrigger className="font-display text-lg text-white hover:text-[#C8A45D] hover:no-underline text-left py-5 px-0">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-white/65 leading-relaxed pb-5 px-0">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="pb-24 lg:pb-32 bg-[#111318]" data-testid="amendment-cta-band">
                <Reveal>
                    <div className="mx-auto max-w-7xl relative border border-[#C8A45D]/40 bg-[#111318] rounded-2xl px-8 sm:px-14 py-16 sm:py-20 overflow-hidden shadow-xl">
                        <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-transparent via-[#C8A45D] to-transparent" />
                        <p className="eyebrow-dark">Questions First?</p>
                        <h2 className="font-display text-3xl sm:text-5xl text-white mt-5 leading-tight max-w-2xl">
                            Not sure if an amendment <em className="italic text-gold-gradient">applies to you?</em>
                        </h2>
                        <p className="text-white/60 leading-relaxed mt-5 max-w-xl">
                            Reach out through our general contact options — a short conversation can save you weeks of
                            wondering.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 mt-10">
                            <GoldButton asChild data-testid="amendment-cta-contact">
                                <Link to="/contact">
                                    Contact Our Team <ArrowRight className="h-4 w-4" />
                                </Link>
                            </GoldButton>
                            {CONTACT.phoneHref && (
                                <GhostButton asChild data-testid="amendment-cta-call">
                                    <a href={CONTACT.phoneHref}>
                                        <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                                    </a>
                                </GhostButton>
                            )}
                            <WhatsAppChatLink testid="amendment-cta-whatsapp">Chat on WhatsApp</WhatsAppChatLink>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
